import React, { FC, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';

import {
  Button,
  CheckBox,
  useCheckBreakpoint,
  headingType,
  size,
  Heading,
  Eyebrow,
  RichText,
  removeParentTagRTE,
} from '@marriott/mi-ui-library';
import { CobrandWebSmallBannerMBoxProps, CobrandWebSmallBannerProps } from './CobrandWebSmallBanner.types';
import { StyledCobrandWebSmallBanner } from './CobrandWebSmallBanner.styles';
import { replaceWhitespaceAndAddBtn, isMobile } from '../../modules/utils';
import { useStore } from '../../modules/store/cobrandStore';
import { CobrandImage } from '../../molecules/CobrandImage/CobrandImage';

export enum ButtonStyle {
  PRIMARY_BUTTON = 'm-button-primary',
  PRIMARY_BUTTON_EXTERNAL = 'm-button-primary m-button-external',
  SECONDARY_BUTTON = 'm-button-secondary',
  SECONDARY_BUTTON_EXTERNAL = 'm-button-secondary m-button-external',
  TERTIARY_BUTTON = 'm-link-tertiary-button',
  TERTIARY_BUTTON_EXTERNAL = 'm-link-tertiary-button-external',
}

export const cobrandwebsmallbannerConfig = {
  emptyLabel: 'CobrandWebSmallBanner',
  isEmpty: () => true,
  resourceType: `/components/content/cobrandwebsmallbanner`,
};

export const CobrandWebSmallBannerComp: FC<CobrandWebSmallBannerProps & CobrandWebSmallBannerMBoxProps> = ({
  openInNewTab = false,
  ctaType,
  ctaLink,
  ctaLinkText,
  header,
  headerTag,
  descriptionText,
  descriptionTextMobile,
  trackingProperties,
  styleclass,
  componentId,
  dynamicMedia,
  openInNewWindowAriaLabel = '',
  customDescriptionClass,
  customContainerClass,
  customImageClass,
  targetData = {},
  cardCode,
  cardHeader,
  cardType,
  imagePathWithoutBadge,
  imagePathWithBadge,
  isComparisonTrayEnabled,
}) => {
  const {
    totalStayLabel = '',
    totalStayAmount = '',
    statementCreditLabel = '',
    statementCreditAmount = '',
    totalAfterCreditLabel = '',
    totalAfterCreditAmount = '',
    pointsLabel = '',
    pointsBalanceLabel = '',
    pointsBalanceAmount = '',
    bonusPointsLabel = '',
    bonusPointsAmount = '',
    pointsTotalLabel = '',
    pointsTotalAmount = '',
    disclaimer = '',
  } = targetData;
  const CobrandWebSmallBannerRef = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
  const isHeader = header !== undefined && header.length > 0 ? true : false;
  const isBodyCopy = descriptionText !== undefined && descriptionText.length > 0 ? true : false;
  const isBodyCopyMobile = descriptionTextMobile !== undefined && descriptionTextMobile.length > 0 ? true : false;
  const isMobileDevice: boolean = isMobile();
  const ENABLE_COMPARISON_TRAY = String(isComparisonTrayEnabled) === 'true';

  // Using removeParentTagRTE to clean the text recieved from RTE
  const headerRTEText =
    isHeader && (header.includes('<') || header.includes('>')) ? removeParentTagRTE(header) : header;

  const isCurrencyMath = Boolean(totalAfterCreditAmount);
  const isPointsMath = Boolean(pointsTotalAmount);
  const isTargetEnabled = isCurrencyMath || isPointsMath;
  const { cobrandData, setCobrandData } = useStore(store => store);
  const isViewportL = useCheckBreakpoint('viewportL');
  const [ComparisonCheckBox, setComparisonCheckBox] = useState(false);
  useEffect(() => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    setComparisonCheckBox(isChecked);
  }, [cobrandData.selectedCards, cardCode]);

  const handleCheckboxChange = () => {
    setComparisonCheckBox(prev => {
      const newCheckState = !prev;
      const updatedCards = newCheckState
        ? [
            ...cobrandData.selectedCards,
            {
              cardname: cardCode,
              checked: newCheckState,
              imagePath: imagePathWithoutBadge || imagePathWithBadge,
              header: cardHeader,
              cardType: cardType,
            },
          ]
        : cobrandData.selectedCards.filter(({ cardname }) => cardname !== cardCode);

      setCobrandData({ ...cobrandData, selectedCards: updatedCards });
      return newCheckState;
    });
  };

  const setDisableState = () => {
    const isChecked = cobrandData.selectedCards.some(item => item.cardname === cardCode);
    return isViewportL && !isMobileDevice
      ? cobrandData.selectedCards.length === 3 && !isChecked
      : cobrandData.selectedCards.length === 2 && !isChecked;
  };

  const getButtonStyle = (ctaType: string, openInNewTab: boolean): string => {
    switch (ctaType) {
      case 'primaryButton':
        return openInNewTab ? ButtonStyle.PRIMARY_BUTTON_EXTERNAL : ButtonStyle.PRIMARY_BUTTON;
      case 'secondaryButton':
        return openInNewTab ? ButtonStyle.SECONDARY_BUTTON_EXTERNAL : ButtonStyle.SECONDARY_BUTTON;
      default:
        return '';
    }
  };

  const buttonTypeClasses = getButtonStyle(ctaType || '', openInNewTab);

  const renderCobrandMathContentSection = (
    pointsOrStayTotalLabel: string,
    pointsOrStayTotalAmt: string,
    bonusOrStatementLabel: string,
    bonusOrStatementAmt: string,
    totalLabel: string,
    totalAmt: string,
    hasPoints: boolean
  ) => {
    return (
      <div className={clsx('d-flex cobrand-math-content-section flex-column flex-grow-1 p-0')}>
        {hasPoints && (
          <div className={clsx('d-flex mb-2 justify-content-end')}>
            <Eyebrow text={pointsLabel} />
          </div>
        )}
        <div className={clsx('d-flex mb-2 justify-content-between')}>
          <RichText text={pointsOrStayTotalLabel} componentId={'points-stay-total-label'} customClass="t-font-alt-xs" />
          <span className="t-font-s">{pointsOrStayTotalAmt}</span>
        </div>
        <div className={clsx('d-flex mb-2 justify-content-between')}>
          <RichText text={bonusOrStatementLabel} componentId={'bonus-statement-label'} customClass="t-font-alt-xs" />
          <span className="t-font-s">{bonusOrStatementAmt}</span>
        </div>
        <div className={clsx('d-flex justify-content-between')}>
          <RichText text={totalLabel} componentId={'total-label'} customClass="t-font-m" />
          <span className="t-font-m total-amount">{totalAmt}</span>
        </div>
      </div>
    );
  };

  return (
    <StyledCobrandWebSmallBanner
      data-component-name="o-cobrand-CobrandWebSmallBanner"
      data-testid="cobrand-CobrandWebSmallBanner"
      id={componentId}
      className={clsx(`col-12 col-sm-8 col-md-12 px-md-0 `, `${styleclass ? styleclass : ''}`)}
    >
      <div
        className={clsx('card-container t-background-color', customContainerClass, {
          'card-math-container': isTargetEnabled,
        })}
      >
        <div
          className={clsx('card-img-container', customImageClass, {
            'math-img-container align-self-lg-center align-self-md-start': isTargetEnabled,
          })}
        >
          <CobrandImage
            defaultImageURL={imagePathWithBadge || dynamicMedia?.assetPath}
            altText={dynamicMedia?.altText}
            customClass="card-img-styles"
          />
        </div>
        <div
          className={clsx(
            'flexible-card-content d-flex flex-grow-1',
            isTargetEnabled
              ? 'flexible-content-section flex-column'
              : 'justify-content-between align-items-center evergreen-content-section'
          )}
        >
          <div className={clsx(isTargetEnabled ? 'd-flex content-section' : 'flexible-card-content-container')}>
            <div className={clsx('flexible-card', { 'align-self-center flexible-math-card': isTargetEnabled })}>
              {isHeader && (
                <Heading
                  customClass={isTargetEnabled ? 'mb-2' : 'mb-1 mb-md-0 ml-md-5'}
                  variation={headingType.subtitle}
                  fontSize={size.large}
                  element={headerTag}
                  dangerouslySet={headerRTEText}
                />
              )}
              {isBodyCopy && descriptionText && (
                <RichText
                  text={descriptionText}
                  componentId={componentId + '-DescriptionText'}
                  customClass={clsx(
                    'd-none d-sm-block pr-md-2 t-font-alt-s ml-md-5',
                    { 'd-block': !descriptionTextMobile },
                    customDescriptionClass
                  )}
                />
              )}
              {isBodyCopyMobile && descriptionTextMobile && (
                <RichText
                  text={descriptionTextMobile}
                  componentId={componentId + '-DescriptionTextMobile'}
                  customClass={clsx('d-block d-sm-none pr-md-2 t-font-alt-s')}
                />
              )}
            </div>
            {isTargetEnabled &&
              isCurrencyMath &&
              renderCobrandMathContentSection(
                totalStayLabel,
                totalStayAmount,
                statementCreditLabel,
                statementCreditAmount,
                totalAfterCreditLabel,
                totalAfterCreditAmount,
                false
              )}
            {isTargetEnabled &&
              isPointsMath &&
              renderCobrandMathContentSection(
                pointsBalanceLabel,
                pointsBalanceAmount,
                bonusPointsLabel,
                bonusPointsAmount,
                pointsTotalLabel,
                pointsBalanceAmount,
                true
              )}
          </div>
          <div
            className={clsx('d-flex', { 'mt-3 disclaimer-and-cta-section justify-content-between': isTargetEnabled })}
          >
            {isTargetEnabled && disclaimer && (
              <RichText
                text={disclaimer}
                componentId={'disclaimer-richtext'}
                customClass="t-font-alt-xs disclaimer-text col-md-5 col-12 p-0"
              />
            )}

            <div
              className={clsx(
                'd-flex justify-content-center align-items-center flex-column',
                !isMobileDevice ? 'flex-lg-row' : ''
              )}
            >
              {ctaLinkText && (
                <Button
                  className={`${buttonTypeClasses} flexible-click m-button-s text-center mt-3 mb-4 my-0 ${
                    !isMobileDevice ? 'm-0' : ''
                  }`}
                  href={ctaLink}
                  isLink={true}
                  target={openInNewTab === true ? '_blank' : '_self'}
                  trackingProperties={trackingProperties}
                  rel={`${openInNewTab ? 'noopener noreferrer' : ''}`}
                  ref={CobrandWebSmallBannerRef}
                  buttonCopy={ctaLinkText}
                  linkAriaLabelOpenNewWindow={openInNewWindowAriaLabel}
                />
              )}
              {ENABLE_COMPARISON_TRAY && (
                <CheckBox
                  checkboxLabel={`Compare`}
                  aria-label={'compare-' + replaceWhitespaceAndAddBtn(componentId)}
                  onChange={handleCheckboxChange}
                  checked={ComparisonCheckBox}
                  checkboxName={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                  checkboxId={replaceWhitespaceAndAddBtn(componentId) + '-checkbox'}
                  className={clsx('mb-5 mb-md-0', !isMobileDevice ? 'ml-5' : '')}
                  disabled={setDisableState()}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledCobrandWebSmallBanner>
  );
};

export const CobrandWebSmallBanner = (props: CobrandWebSmallBannerProps) => {
  return (
    <EditableComponent config={cobrandwebsmallbannerConfig} {...props}>
      <CobrandWebSmallBannerComp {...props} />
    </EditableComponent>
  );
};
